// Verträge: die gemeinsame Sprache von Oberfläche, HTTP-API, KI-Assistent, Import und Jobs.
// Ein Vertrag beschreibt eine Aktion ohne Servercode (Name, Texte, Zod-Schemata, Recht, Risiko, Standortbezug)
// und ist deshalb auch im Browser importierbar. Die Implementierung bindet der Server mit defineAction().
import { z } from 'zod'
import type { ErrorPayload } from './errors'

// ---- Prüfmeldungen für Menschen ---------------------------------------------------------------------------
// Zod liefert für Regeln ohne eigene Meldung Sätze für Entwickler („Ungültige Eingabe: erwartet string, erhalten
// undefined“, „Zu klein: erwartet, dass string >=3 Zeichen hat“). Sie stehen im Browser am Feld und in den
// Feldfehlern des Servers (VALIDATION, details.fields) – also auch bei API-Kunden und im Assistenten. Hier, an der
// EINEN Stelle, wird daraus ein Satz, der sagt, was zu tun ist (design-system §6.8). Reihenfolge in Zod: eigene
// Meldung am Schema ({ error: '…' }) → diese Abbildung → deutscher Standardsatz (z.locales.de()).
// Für Geld und Mengen (Cent, Tausendstel) immer eine eigene Meldung in den Vertrag schreiben – „mindestens 1“ wäre
// dort irreführend.

export const REQUIRED_MESSAGE = 'Bitte füllen Sie dieses Feld aus.'

const formatLimit = (value: unknown): string =>
  typeof value === 'number' || typeof value === 'bigint' ? value.toLocaleString('de-DE') : String(value)

/** Satz für Menschen zu einem Zod-Issue – oder undefined, dann gilt der deutsche Standardsatz von Zod. */
export function validationMessage(issue: {
  code?: string
  input?: unknown
  expected?: unknown
  origin?: unknown
  minimum?: unknown
  maximum?: unknown
  inclusive?: unknown
  format?: unknown
}): string | undefined {
  const origin = typeof issue.origin === 'string' ? issue.origin : ''
  const numeric = origin === 'number' || origin === 'int' || origin === 'bigint'
  const list = origin === 'array' || origin === 'set'
  switch (issue.code) {
    case 'invalid_type':
      if (issue.input === undefined || issue.input === null) return REQUIRED_MESSAGE
      if (issue.expected === 'int') return 'Bitte eine ganze Zahl eingeben.'
      if (issue.expected === 'number' || issue.expected === 'bigint') return 'Bitte eine Zahl eingeben.'
      if (issue.expected === 'date') return 'Bitte ein gültiges Datum eingeben.'
      return 'Diese Eingabe ist nicht gültig.'
    case 'too_small':
      if (origin === 'string') {
        return issue.minimum === 1 ? REQUIRED_MESSAGE : `Bitte mindestens ${formatLimit(issue.minimum)} Zeichen eingeben.`
      }
      if (numeric) {
        return issue.inclusive === false
          ? `Der Wert muss größer als ${formatLimit(issue.minimum)} sein.`
          : `Der Wert muss mindestens ${formatLimit(issue.minimum)} sein.`
      }
      if (list) {
        return issue.minimum === 1
          ? 'Bitte mindestens einen Eintrag angeben.'
          : `Bitte mindestens ${formatLimit(issue.minimum)} Einträge angeben.`
      }
      return 'Der Wert liegt außerhalb des erlaubten Bereichs.'
    case 'too_big':
      if (origin === 'string') return `Bitte höchstens ${formatLimit(issue.maximum)} Zeichen eingeben.`
      if (numeric) {
        return issue.inclusive === false
          ? `Der Wert muss kleiner als ${formatLimit(issue.maximum)} sein.`
          : `Der Wert darf höchstens ${formatLimit(issue.maximum)} sein.`
      }
      if (list) return `Höchstens ${formatLimit(issue.maximum)} Einträge sind möglich.`
      return 'Der Wert liegt außerhalb des erlaubten Bereichs.'
    case 'invalid_value':
      return 'Bitte wählen Sie einen Wert aus der Liste.'
    case 'invalid_format':
      if (issue.format === 'email') return 'Bitte eine gültige E-Mail-Adresse eingeben.'
      if (issue.format === 'date' || issue.format === 'datetime') return 'Bitte ein gültiges Datum eingeben.'
      if (issue.format === 'time') return 'Bitte eine gültige Uhrzeit eingeben.'
      if (issue.format === 'url') return 'Bitte eine gültige Adresse (URL) eingeben.'
      return 'Diese Eingabe hat nicht die erwartete Form.'
    case 'unrecognized_keys':
      return 'Die Eingabe enthält ein Feld, das hier nicht vorgesehen ist.'
    case 'not_multiple_of':
      return 'Der Wert passt nicht zur erlaubten Schrittweite.'
    default:
      return undefined
  }
}

// Deutsche Prüfmeldungen überall dort, wo Verträge geladen werden (Server und Browser).
z.config({ ...z.locales.de(), customError: (issue) => validationMessage(issue) })

// ---- Grundbegriffe ----------------------------------------------------------

/** read: nur lesen · write: ändert Daten · destructive: löscht/storniert · financial: bucht Geld oder Bestandswert */
export const RISK_CLASSES = ['read', 'write', 'destructive', 'financial'] as const
export type RiskClass = (typeof RISK_CLASSES)[number]

/** Über welchen Weg eine Aktion ausgelöst wurde – steht in jedem Audit-Eintrag. */
export const CHANNELS = ['ui', 'api', 'assistant', 'assistant-proactive', 'import', 'system'] as const
export type Channel = (typeof CHANNELS)[number]

/**
 * Kanäle, die ein Vertrag für seine Audit-Einträge und Bestandsbuchungen festlegen kann (`auditChannel`) – zusätzlich
 * zu CHANNELS. 'pos-offline': Übernahme der Vorgänge einer Kasse ohne Verbindung (docs/kasse-offline/SPEZIFIKATION.md
 * §14.5 Nr. 4; Datenbank: audit_log.channel, stock_posting.channel). Rechte, Standortbezug und Bestätigungen richten
 * sich weiter nach dem Aufrufweg (Channel).
 */
export const AUDIT_CHANNEL_OVERRIDES = ['pos-offline'] as const
export type AuditChannel = (typeof AUDIT_CHANNEL_OVERRIDES)[number]

/** Rechteschlüssel, z. B. 'inventory.adjustment.create'. Deklariert in modules/<id>/module.ts. */
export type PermissionKey = string

/** Sonderwerte für `permission`: jede angemeldete Mitgliedschaft bzw. ganz ohne Anmeldung (Login). */
export const PERMISSION_AUTHENTICATED = '@authenticated'
export const PERMISSION_PUBLIC = '@public'
/** Einstandspreise und Roherträge sehen – mandantenweit, deklariert vom Modul „platform“ (Feldrechte, s. u.). */
export const PERMISSION_COSTS_READ = 'costs.read'

/**
 * Standortbezug einer Aktion. Der Dispatcher prüft ihn nach dem Recht:
 *   Standort-IDs  das Recht muss für ALLE genannten Standorte gelten (leere Liste = wie 'any')
 *   'tenant'      kein Standortbezug → das Recht muss unbeschränkt (für alle Standorte) gelten. Nur für Rechte mit
 *                 `locationScoped: false` zulässig (sonst meldet die Registry einen Fehler)
 *   'any'         nur lesende Aktionen: das Recht genügt für irgendeinen Standort; die Aktion filtert selbst mit
 *                 ctx.locationsFor(recht) (Listen, Suchen)
 *   'record'      der Standort steht im Datensatz, nicht in der Eingabe: defineAction(..., { resolveScope })
 *                 liefert die Standorte in der Transaktion vor dem Handler
 */
export type LocationScope = readonly string[] | 'tenant' | 'any' | 'record'
/** Wie der Standortbezug im Vertrag angegeben ist ('input' = Funktion der Eingabe). */
export type ScopeKind = 'input' | 'tenant' | 'any' | 'record'

/** Wirkungs-Merkmale zusätzlich zur Risikoklasse – steuern Bestätigungsart und Rückgängig-Angebot. */
export interface ActionEffects {
  /** verlässt das System (E-Mail, Druck, Webhook, EDI) */
  external: boolean
  /** es gibt eine Gegenaktion (Storno, Wiederherstellen) */
  reversible: boolean
  /** Rollen, Rechte, API-Schlüssel, Bankdaten, Nummernkreise */
  securitySensitive: boolean
}

/** Texte für das Sprachmodell – getrennt von den Oberflächentexten. */
export interface LlmHints<I = unknown> {
  /** 2–4 Sätze: was die Aktion tut, wann sie zu verwenden ist und wann nicht („Verwenden, wenn …“) */
  description: string
  /** Wörter, die Mitarbeiter tatsächlich sagen (Bruch, Schwund, Nachschub, Leergut) – für die Werkzeugsuche */
  keywords: readonly string[]
  examples?: readonly I[]
  /** Rollenprofile, in denen das Werkzeug immer geladen ist */
  coreFor?: readonly string[]
}

export type AssistantExposure = 'default' | 'opt-in' | 'never'

// ---- Aktionsvertrag ---------------------------------------------------------

export interface ActionContract<
  N extends string = string,
  I extends z.ZodType = z.ZodType,
  O extends z.ZodType = z.ZodType,
> {
  readonly kind: 'action'
  /** eindeutig im ganzen System: '<modul>.<gegenstand>.<verb>' */
  readonly name: N
  /** Modul-ID = erstes Namenssegment */
  readonly module: string
  /** deutsch, Oberfläche: „Korrekturbuchung erfassen“ */
  readonly title: string
  /** deutsch, Oberfläche und Hilfe */
  readonly description: string
  readonly llm: LlmHints<z.input<I>>
  readonly input: I
  readonly output: O
  readonly permission: PermissionKey
  readonly risk: RiskClass
  /** Methode (nicht Property), damit Verträge mit konkreter Eingabe in Listen von AnyActionContract passen. */
  scope(input: z.output<I>): LocationScope
  readonly scopeKind: ScopeKind
  readonly effects: ActionEffects
  /** Funktionsschalter, der an sein muss (sonst FEATURE_DISABLED) */
  readonly feature: string | null
  readonly assistant: AssistantExposure
  readonly limits: ActionLimits<z.output<I>> | null
  /** Feldrechte der Ausgabe: der Dispatcher entfernt diese Felder, wenn dem Nutzer das Recht fehlt. */
  readonly fieldPermissions: readonly FieldPermissionRule[]
  /** 'required' (Standard): ändernde Aktionen schreiben mindestens einen Audit-Eintrag · 'none': siehe Spec */
  readonly audit: 'required' | 'none'
  /** Kanal der Audit-Einträge und Bestandsbuchungen dieser Aktion statt des Aufrufwegs; null = Aufrufweg (siehe Spec) */
  readonly auditChannel: AuditChannel | null
  /** technischer Zustand mit Fachrecht (erlaubt audit 'none', siehe Spec) */
  readonly technicalState: boolean
  /** true: mit API-Schlüssel unter /api/public/v1/actions/<name> aufrufbar (öffentliche API) */
  readonly publicApi: boolean
  /**
   * 'full' (Standard): Der Dispatcher legt die bereinigte Eingabe in jeden Audit-Eintrag dieser Aktion (audit_log.input).
   * 'omit': keine Eingabe im Audit – für Personendaten, die nicht im versiegelten Protokoll stehen dürfen (Interessent,
   * Postleitzahl der Auftragsauskunft am Telefon). Der Klartext des Eintrags nennt dann ebenfalls keine.
   */
  readonly auditInput: 'full' | 'omit'
  /**
   * true: Die Aktion lässt sich an Ort und Stelle von einer zweiten Person freigeben – sie weist sich mit ihrer PIN
   * aus, die Aktion läuft dann mit IHREN Rechten (Aufsicht an der Kasse). Standard: false.
   */
  readonly secondPerson: boolean
}

export interface ActionLimits<I = unknown> {
  /** Massengrenze: mehr Elemente → LIMIT_EXCEEDED */
  maxItems?: number
  itemCount?(input: I): number
  /**
   * Werte der Eingabe, die gegen die Grenzen der Rolle geprüft werden (role_permission.constraints):
   * Schlüssel = Grenze, die das Recht der Aktion deklariert (definePermission({ limits })). Der Dispatcher prüft
   * vor dem Handler; Werte, die erst im Handler feststehen, prüft ctx.requireWithin().
   */
  values?(input: I): Partial<Record<string, number>>
  /**
   * Größter Rumpf einer Anfrage an diese Aktion in Byte (HTTP, beide Wege: Oberfläche und öffentliche API). Standard
   * 1 MB; höchstens MAX_ACTION_BODY_BYTES – nur für Verträge mit Bildern (Zustellnachweis mit Unterschrift und Foto).
   */
  bodyBytes?: number
}

/** Obergrenze für `limits.bodyBytes` (4 MB) – größere Dateien gehen über den Datei-Upload */
export const MAX_ACTION_BODY_BYTES = 4 * 1024 * 1024
/** Standardgrenze des Rumpfs einer Aktion (1 MB) */
export const DEFAULT_ACTION_BODY_BYTES = 1024 * 1024

/** Ein Ausgabefeld, das nur mit einem Recht sichtbar ist. `path` in Punkt-Schreibweise, `*` für Listenelemente. */
export interface FieldPermissionRule {
  readonly path: string
  readonly segments: readonly string[]
  readonly permission: PermissionKey
  /** omit: Feld entfällt (optional im Schema) · null: Feld wird null (nullable im Schema) */
  readonly strip: 'omit' | 'null'
}

export interface ActionContractSpec<N extends string, I extends z.ZodType, O extends z.ZodType> {
  name: N
  title: string
  description: string
  llm: LlmHints<z.input<I>>
  input: I
  output: O
  permission: PermissionKey
  risk: RiskClass
  /**
   * Standortbezug (siehe LocationScope): Funktion der Eingabe oder 'tenant' | 'any' | 'record'.
   * Standard: 'tenant'. Für standortbezogene Rechte Pflicht (die Registry meldet 'tenant' dort als Fehler).
   */
  scope?: ((input: z.output<I>) => readonly string[] | 'tenant' | 'any') | 'tenant' | 'any' | 'record'
  /** Standard: alles false. Bei risk 'read' nicht erlaubt. */
  effects?: Partial<ActionEffects>
  feature?: string
  /** Standard: 'default' */
  assistant?: AssistantExposure
  limits?: ActionLimits<z.output<I>>
  /**
   * Feldrechte: Ausgabepfad → Recht, z. B. { 'lines.*.costCents': 'costs.read', marginCents: 'costs.read' }.
   * Das Feld muss im Ausgabeschema optional oder nullable sein; das Recht gilt mandantenweit (locationScoped: false).
   */
  fieldPermissions?: Record<string, PermissionKey>
  /**
   * 'none' nimmt eine Aktion von der Audit-Pflicht aus. Nur für persönlichen Zustand ohne Geschäftsbezug
   * (gespeicherte Ansicht, Gelesen-Markierung): risk 'write', Recht '@authenticated', keine Wirkungs-Merkmale.
   */
  audit?: 'required' | 'none'
  /**
   * Technischer Zustand mit Fachrecht (docs/kasse-offline/SPEZIFIKATION.md §14.5 Nr. 5): erlaubt `audit: 'none'` auch
   * mit einem Fachrecht, wenn risk 'write' und keine Wirkungs-Merkmale (external, reversible, securitySensitive) gesetzt
   * sind – für Aufrufe, die nur einen technischen Zustand ohne Historie fortschreiben (Herzschlag eines Kassengeräts).
   * Zustandswechsel, die fachlich zählen, schreibt der Handler selbst als Audit-Eintrag. Standard: false.
   */
  technicalState?: boolean
  /**
   * Kanal der Audit-Einträge und Bestandsbuchungen dieser Aktion (und der Aktionen, die sie mit ctx.call aufruft):
   * 'pos-offline' für die Übernahme von Vorgängen einer Kasse ohne Verbindung (§14.5 Nr. 4). Nur der Kanal der
   * Protokolle ändert sich; Rechte, Standortbezug, Bestätigungen und ctx.channel bleiben beim Aufrufweg. Kern-Optionen,
   * die nur für diese Übernahme gelten (documents.post({ offlineTakeover })), prüfen ihn. Nicht für lesende Aktionen.
   */
  auditChannel?: AuditChannel
  /**
   * Gibt die Aktion für die öffentliche API frei: POST /api/public/v1/actions/<name> mit API-Schlüssel
   * (Kanal 'api', dieselben Rechte und Prüfungen). Standard: false – ohne die Angabe ist die Aktion dort unbekannt.
   */
  publicApi?: boolean
  /**
   * 'omit': Die Eingabe steht nicht im Audit-Protokoll (audit_log.input bleibt leer) – für Personendaten aus einem Kanal,
   * die nach einer Frist gelöscht werden müssen (das Protokoll ist versiegelt und vom Löschkonzept ausgenommen). Nur mit
   * `assistant: 'never'`. Standard: 'full'.
   */
  auditInput?: 'full' | 'omit'
  /**
   * Freigabe durch eine zweite Person an Ort und Stelle (POST /api/actions/<name> mit `approval: { userId, pin }`):
   * Die Aktion läuft dann mit den Rechten der zweiten Person, im Audit steht „freigegeben von …“, der Handler liest
   * sie aus `ctx.approvedBy`. Nur für ändernde Aktionen, die genau einen Vorgang betreffen (Rabatt, Storno) – nie
   * für Sammelaktionen, Einstellungen oder Rechte. Im Kanal des Assistenten gibt es die Freigabe nicht.
   */
  secondPerson?: boolean
}

export type AnyActionContract = ActionContract<string, z.ZodType, z.ZodType>

/** Eingabe, wie der Handler sie erhält (nach Zod: Standardwerte gesetzt, umgewandelt). */
export type InputOf<C extends AnyActionContract> = z.output<C['input']>
/** Eingabe, wie Aufrufer sie übergeben (vor Zod). */
export type InputArg<C extends AnyActionContract> = z.input<C['input']>
/** Ergebnis, wie Aufrufer es erhalten (nach Zod). */
export type OutputOf<C extends AnyActionContract> = z.output<C['output']>
/** Ergebnis, wie der Handler es zurückgibt (vor Zod). */
export type OutputArg<C extends AnyActionContract> = z.input<C['output']>

const ACTION_NAME = /^[a-z][a-z0-9]*(\.[a-z][a-zA-Z0-9]*){1,3}$/
const MODULE_ID = /^[a-z][a-z0-9]*$/
const KEY = /^[a-z][a-z0-9]*(\.[a-z][a-zA-Z0-9]*){1,4}$/

function fail(subject: string, message: string): never {
  throw new Error(`[Vertrag ${subject}] ${message}`)
}

const isSchema = (value: unknown): boolean => value instanceof z.ZodType

interface SchemaNode {
  def?: { type?: string; innerType?: SchemaNode }
  shape?: Record<string, SchemaNode>
  element?: SchemaNode
}

const WRAPPERS = new Set(['optional', 'nullable', 'default', 'prefault', 'readonly', 'nonoptional', 'catch'])

function unwrapSchema(node: SchemaNode | undefined): SchemaNode | undefined {
  let current = node
  while (current?.def?.type && WRAPPERS.has(current.def.type) && current.def.innerType) current = current.def.innerType
  return current
}

/** Löst einen Ausgabepfad ('lines.*.costCents') im Zod-Schema auf; undefined = Pfad gibt es nicht. */
function schemaAtPath(schema: z.ZodType, segments: readonly string[]): z.ZodType | undefined {
  let current: SchemaNode | undefined = schema as unknown as SchemaNode
  for (const segment of segments) {
    const inner = unwrapSchema(current)
    if (segment === '*') current = inner?.def?.type === 'array' ? inner.element : undefined
    else current = inner?.def?.type === 'object' ? inner.shape?.[segment] : undefined
    if (!current) return undefined
  }
  return current as unknown as z.ZodType
}

function fieldRules(subject: string, output: z.ZodType, spec: Record<string, PermissionKey>): FieldPermissionRule[] {
  return Object.entries(spec).map(([path, permission]) => {
    const segments = path.split('.')
    const leaf = schemaAtPath(output, segments)
    if (!leaf || segments.at(-1) === '*') fail(subject, `fieldPermissions: Den Ausgabepfad '${path}' gibt es nicht.`)
    const strip = leaf.safeParse(undefined).success ? 'omit' : leaf.safeParse(null).success ? 'null' : null
    if (!strip) fail(subject, `fieldPermissions: Das Feld '${path}' muss im Ausgabeschema optional oder nullable sein.`)
    if (!permission?.trim()) fail(subject, `fieldPermissions: Für '${path}' fehlt das Recht.`)
    return Object.freeze({ path, segments: Object.freeze(segments), permission, strip })
  })
}

/**
 * Definiert den Vertrag einer Aktion. Prüft die Angaben sofort beim Laden –
 * ein fehlerhafter Vertrag lässt den Start scheitern, nicht erst den ersten Aufruf.
 */
export function defineContract<const N extends string, I extends z.ZodType, O extends z.ZodType>(
  spec: ActionContractSpec<N, I, O>,
): ActionContract<N, I, O> {
  const subject = String(spec.name)
  if (!ACTION_NAME.test(spec.name)) {
    fail(subject, "Der Name muss die Form '<modul>.<gegenstand>.<verb>' haben (2–4 Segmente, klein beginnend).")
  }
  if (!spec.title?.trim()) fail(subject, 'title (deutsch) fehlt.')
  if (!spec.description?.trim()) fail(subject, 'description (deutsch) fehlt.')
  if (!spec.llm?.description?.trim()) fail(subject, 'llm.description fehlt.')
  if (!Array.isArray(spec.llm.keywords)) fail(subject, 'llm.keywords fehlt (Liste, darf leer sein).')
  if (!isSchema(spec.input) || !isSchema(spec.output)) fail(subject, 'input und output müssen Zod-Schemata sein.')
  if (!(RISK_CLASSES as readonly string[]).includes(spec.risk)) fail(subject, `Unbekannte Risikoklasse: ${spec.risk}`)
  if (!spec.permission?.trim()) fail(subject, 'permission fehlt.')
  const bodyBytes = spec.limits?.bodyBytes
  if (bodyBytes !== undefined && (!Number.isInteger(bodyBytes) || bodyBytes < DEFAULT_ACTION_BODY_BYTES || bodyBytes > MAX_ACTION_BODY_BYTES)) {
    fail(subject, `limits.bodyBytes liegt zwischen ${DEFAULT_ACTION_BODY_BYTES} und ${MAX_ACTION_BODY_BYTES} Byte.`)
  }
  const effects: ActionEffects = {
    external: spec.effects?.external ?? false,
    reversible: spec.effects?.reversible ?? false,
    securitySensitive: spec.effects?.securitySensitive ?? false,
  }
  if (spec.risk === 'read' && (effects.external || effects.reversible || effects.securitySensitive)) {
    fail(subject, "Eine lesende Aktion (risk 'read') hat keine Wirkungs-Merkmale.")
  }
  const scope = spec.scope
  const scopeKind: ScopeKind = typeof scope === 'function' ? 'input' : (scope ?? 'tenant')
  if (!['input', 'tenant', 'any', 'record'].includes(scopeKind)) fail(subject, `Unbekannter Standortbezug: ${scopeKind}`)
  if (scopeKind === 'any' && spec.risk !== 'read') {
    fail(subject, "scope 'any' gibt es nur für lesende Aktionen – ändernde nennen die Standorte oder nutzen 'record'.")
  }
  const audit = spec.audit ?? 'required'
  const plainWrite = spec.risk === 'write' && !effects.external && !effects.reversible && !effects.securitySensitive
  const technicalState = spec.technicalState === true
  if (technicalState && !plainWrite) {
    fail(subject, "technicalState gibt es nur für technischen Zustand: risk 'write' ohne Wirkungs-Merkmale.")
  }
  if (audit === 'none') {
    const personal = plainWrite && spec.permission === PERMISSION_AUTHENTICATED
    if (!personal && !technicalState) {
      fail(
        subject,
        "audit: 'none' gibt es nur für persönlichen Zustand (risk 'write', Recht '@authenticated', keine Merkmale) oder technischen Zustand (technicalState, risk 'write', keine Merkmale).",
      )
    }
  }
  const auditChannel = spec.auditChannel ?? null
  if (auditChannel !== null && !(AUDIT_CHANNEL_OVERRIDES as readonly string[]).includes(auditChannel)) {
    fail(subject, `Unbekannter Kanal für das Protokoll: ${String(auditChannel)}`)
  }
  if (auditChannel !== null && spec.risk === 'read') fail(subject, 'auditChannel gibt es nur für ändernde Aktionen.')
  if (spec.secondPerson === true && (spec.risk === 'read' || effects.securitySensitive)) {
    fail(subject, 'secondPerson gibt es nur für ändernde Aktionen an einem einzelnen Vorgang – nicht für lesende oder sicherheitsrelevante Aktionen.')
  }
  const auditInput = spec.auditInput ?? 'full'
  if (auditInput !== 'full' && auditInput !== 'omit') fail(subject, `Unbekannte Angabe für auditInput: ${String(auditInput)}`)
  if (auditInput === 'omit' && (spec.assistant ?? 'default') !== 'never') {
    fail(subject, "auditInput: 'omit' gibt es nur mit assistant: 'never' – der Verlauf des Assistenten lebt von der protokollierten Eingabe.")
  }
  const literalScope = scopeKind === 'input' ? 'tenant' : scopeKind
  const fieldPermissions = spec.fieldPermissions ? fieldRules(subject, spec.output, spec.fieldPermissions) : []
  return Object.freeze({
    kind: 'action' as const,
    name: spec.name,
    module: spec.name.split('.')[0] as string,
    title: spec.title,
    description: spec.description,
    llm: spec.llm,
    input: spec.input,
    output: spec.output,
    permission: spec.permission,
    risk: spec.risk,
    scope: typeof scope === 'function' ? (input: z.output<I>) => scope(input) : () => literalScope,
    scopeKind,
    effects: Object.freeze(effects),
    feature: spec.feature ?? null,
    assistant: spec.assistant ?? 'default',
    limits: spec.limits ?? null,
    fieldPermissions: Object.freeze(fieldPermissions),
    audit,
    auditChannel,
    technicalState,
    publicApi: spec.publicApi === true,
    auditInput,
    secondPerson: spec.secondPerson === true,
  })
}

export const isActionContract = (value: unknown): value is AnyActionContract =>
  typeof value === 'object' && value !== null && (value as { kind?: unknown }).kind === 'action'

// ---- Fachereignisse ---------------------------------------------------------

export interface EventContract<N extends string = string, P extends z.ZodType = z.ZodType> {
  readonly kind: 'event'
  /** '<modul>.<gegenstand>.<partizip>' – z. B. 'sales.invoice.posted' */
  readonly name: N
  readonly module: string
  readonly title: string
  readonly payload: P
}

export type AnyEventContract = EventContract<string, z.ZodType>
export type PayloadOf<E extends AnyEventContract> = z.output<E['payload']>
export type PayloadArg<E extends AnyEventContract> = z.input<E['payload']>

export function defineEvent<const N extends string, P extends z.ZodType>(spec: {
  name: N
  title: string
  payload: P
}): EventContract<N, P> {
  if (!ACTION_NAME.test(spec.name))
    fail(spec.name, "Der Ereignisname muss die Form '<modul>.<gegenstand>.<partizip>' haben.")
  if (!spec.title?.trim()) fail(spec.name, 'title (deutsch) fehlt.')
  if (!isSchema(spec.payload)) fail(spec.name, 'payload muss ein Zod-Schema sein.')
  return Object.freeze({
    kind: 'event' as const,
    name: spec.name,
    module: spec.name.split('.')[0] as string,
    title: spec.title,
    payload: spec.payload,
  })
}

export const isEventContract = (value: unknown): value is AnyEventContract =>
  typeof value === 'object' && value !== null && (value as { kind?: unknown }).kind === 'event'

// ---- Rechte, Funktionsschalter, Modul-Manifest ---------------------------------

export interface PermissionDef {
  readonly kind: 'permission'
  readonly key: PermissionKey
  /** deutsch, für die Rechte-Matrix: „Korrekturbuchungen erfassen“ */
  readonly label: string
  /** deutsch, Gruppierung in der Rechte-Matrix: „Lager“ */
  readonly group: string
  readonly description: string | null
  /** true: das Recht lässt sich je Standort vergeben · false: gilt immer mandantenweit */
  readonly locationScoped: boolean
  /** sicherheitskritisch (Rollen, Rechte, Schlüssel) – wird in der Matrix hervorgehoben */
  readonly sensitive: boolean
  /** Grenzen, die sich je Rolle an diesem Recht einstellen lassen (role_permission.constraints) */
  readonly limits: readonly PermissionLimitDef[]
}

/**
 * Eine Grenze am Recht – immer ein Höchstwert (größer = mehr erlaubt). Fehlt der Wert in der Rolle, gilt keine
 * Grenze. Hält ein Nutzer das Recht über mehrere Rollen, gilt für einen Standort der großzügigste Wert.
 */
export interface PermissionLimitDef {
  /** z. B. 'maxDiscountBp', 'maxAdjustmentCents' */
  readonly key: string
  /** deutsch, für die Rechte-Matrix: „Höchster Rabatt“ */
  readonly label: string
  /** bp: Basispunkte · cents: Cent · qty: Tausendstel der Basiseinheit · count: Anzahl */
  readonly unit: 'bp' | 'cents' | 'qty' | 'count'
}

export function definePermission(spec: {
  key: PermissionKey
  label: string
  group: string
  description?: string
  locationScoped?: boolean
  sensitive?: boolean
  limits?: readonly PermissionLimitDef[]
}): PermissionDef {
  if (!KEY.test(spec.key)) fail(spec.key, "Der Rechteschlüssel muss die Form '<bereich>.<gegenstand>.<verb>' haben.")
  if (!spec.label?.trim()) fail(spec.key, 'label (deutsch) fehlt.')
  if (!spec.group?.trim()) fail(spec.key, 'group (deutsch) fehlt.')
  const limitKeys = new Set<string>()
  for (const limit of spec.limits ?? []) {
    if (!/^[a-z][a-zA-Z0-9]*$/.test(limit.key)) fail(spec.key, `Ungültiger Schlüssel einer Grenze: ${limit.key}`)
    if (!limit.label?.trim()) fail(spec.key, `Grenze ${limit.key}: label (deutsch) fehlt.`)
    if (limitKeys.has(limit.key)) fail(spec.key, `Grenze doppelt: ${limit.key}`)
    limitKeys.add(limit.key)
  }
  return Object.freeze({
    kind: 'permission' as const,
    key: spec.key,
    label: spec.label,
    group: spec.group,
    description: spec.description ?? null,
    locationScoped: spec.locationScoped ?? true,
    sensitive: spec.sensitive ?? false,
    limits: Object.freeze((spec.limits ?? []).map((limit) => Object.freeze({ ...limit }))),
  })
}

export interface FeatureDef {
  readonly kind: 'feature'
  /** z. B. 'deposits' oder 'sales.consignment' */
  readonly key: string
  readonly label: string
  readonly description: string | null
  /** Voreinstellung für neue Mandanten (Branchenprofile dürfen überschreiben) */
  readonly defaultEnabled: boolean
  /**
   * Was der Server mit dem Schalter durchsetzt, wenn er keine Aktionen oder Module ausblendet (ein Satz für
   * Einstellungen › Funktionen, z. B. „Beim Festschreiben entsteht eine XRechnung“). Ohne Angabe gilt ein Schalter dort
   * nur als wirksam, wenn Module oder Aktionen an ihm hängen.
   */
  readonly effect: string | null
}

export function defineFeature(spec: {
  key: string
  label: string
  description?: string
  defaultEnabled?: boolean
  effect?: string
}): FeatureDef {
  if (!/^[a-z][a-z0-9]*(\.[a-z][a-zA-Z0-9]*){0,3}$/.test(spec.key))
    fail(spec.key, 'Ungültiger Schlüssel für einen Funktionsschalter.')
  if (!spec.label?.trim()) fail(spec.key, 'label (deutsch) fehlt.')
  return Object.freeze({
    kind: 'feature' as const,
    key: spec.key,
    label: spec.label,
    description: spec.description ?? null,
    defaultEnabled: spec.defaultEnabled ?? true,
    effect: spec.effect?.trim() || null,
  })
}

/** Manifest eines Moduls – `export default defineModule({...})` in modules/<id>/module.ts. */
export interface ModuleManifest {
  readonly kind: 'module'
  /** = Ordnername unter modules/ und erstes Segment aller Aktions- und Ereignisnamen des Moduls */
  readonly id: string
  readonly title: string
  readonly description: string | null
  readonly permissions: readonly PermissionDef[]
  readonly features: readonly FeatureDef[]
  /** Modul-IDs, deren Verträge dieses Modul aufruft (ctx.call) oder deren Ereignisse es abonniert */
  readonly dependsOn: readonly string[]
  /**
   * Modul-IDs, deren Verträge dieses Modul NUR aufruft, wenn sie beim Mandanten aktiv sind (Branchenmodule wie
   * Pfand & Leergut). Anders als bei dependsOn bleibt das Modul ohne sie aktiv; jeder Aufruf steht hinter
   * ctx.feature(<schalter>) bzw. useFeature().
   */
  readonly optionalDependsOn: readonly string[]
  /** Branchenmodul: nur aktiv, wenn dieser Funktionsschalter an ist; null = immer aktiv */
  readonly requiresFeature: string | null
}

export function defineModule(spec: {
  id: string
  title: string
  description?: string
  permissions?: readonly PermissionDef[]
  features?: readonly FeatureDef[]
  dependsOn?: readonly string[]
  optionalDependsOn?: readonly string[]
  requiresFeature?: string
}): ModuleManifest {
  if (!MODULE_ID.test(spec.id))
    fail(spec.id, 'Die Modul-ID besteht aus Kleinbuchstaben und Ziffern (wie der Ordnername).')
  if (!spec.title?.trim()) fail(spec.id, 'title (deutsch) fehlt.')
  const keys = new Set<string>()
  for (const permission of spec.permissions ?? []) {
    if (keys.has(permission.key)) fail(spec.id, `Rechteschlüssel doppelt: ${permission.key}`)
    keys.add(permission.key)
  }
  return Object.freeze({
    kind: 'module' as const,
    id: spec.id,
    title: spec.title,
    description: spec.description ?? null,
    permissions: Object.freeze([...(spec.permissions ?? [])]),
    features: Object.freeze([...(spec.features ?? [])]),
    dependsOn: Object.freeze([...(spec.dependsOn ?? [])]),
    optionalDependsOn: Object.freeze([...(spec.optionalDependsOn ?? [])]),
    requiresFeature: spec.requiresFeature ?? null,
  })
}

export const isModuleManifest = (value: unknown): value is ModuleManifest =>
  typeof value === 'object' && value !== null && (value as { kind?: unknown }).kind === 'module'

// ---- Gemeinsame Datenformen -------------------------------------------------------

/** Verweis auf einen Datensatz (Audit, Vorschau, Verknüpfungen). `type` = Tabellen-/Belegartname. */
export interface RecordRef {
  type: string
  id: string
  /** deutscher Klartext: „Rechnung RE-2026-00042“ */
  label?: string
}

/** Treffer der globalen Suche. */
export interface SearchHit {
  /** Art des Treffers, z. B. 'item', 'partner', 'document' – bestimmt Darstellung und Ziel */
  entity: string
  id: string
  title: string
  subtitle?: string
  /** Kennung in Mono-Schrift (Artikelnummer, Belegnummer) */
  code?: string
  /** Unterart – bei `entity: 'document'` die Belegart; die Oberfläche fragt erst `<entity>:<kind>`, dann `<entity>` */
  kind?: string
  score?: number
}

/** Transport: POST /api/actions/<name> */
export interface ActionRequest<I = unknown> {
  input: I
  idempotencyKey?: string
  /** effectHash der bestätigten Vorschau; weicht die Wirkung inzwischen ab → CONFLICT mit neuer Vorschau */
  expectedEffectHash?: string
}

export type ActionResponse<O = unknown> = { result: O; error?: undefined } | { error: ErrorPayload; result?: undefined }
